/**
 * Ledger transactions (redesign 2026-10-05) — shown only after the admin
 * asks for the selected period. Newest first, server-paged 25 at a time with
 * a client-side cursor stack (Previous / Next / Page N). Compact rows that
 * never scroll sideways at ≥1280px and stack into two lines below that; a
 * row expands inline into {@link LedgerRunDetails}.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { RubyLoader } from '../../../components/RubyLoader';
import SearchableDropdown from '../../../components/SearchableDropdown';
import {
  getRubyLedger, getRubyLedgerRun,
  type LedgerFilters, type LedgerLine, type LedgerRunDetail,
} from '../../../services/aiControl/ledger';
import type { AiCatalogResponse } from '../../../services/aiControl/rubyAi';
import {
  ALL, LEDGER_PAGE_SIZE, actionFilterOptions, actionLabel, firstPage, formatTime, itemLabel, locationLabel,
  merchantName, modelFilterOptions, modelLabel, money, nextPage, pageRange, prevPage, profitTone,
  recordPageCount, shortModel, statusLabel, type LedgerPager,
} from './ledgerModel';
import { tokensLabel } from '../rubyAi/units';
import { ErrorBanner } from '../rubyAi/ui';
import { Pill } from './LedgerPeriodPicker';
import LedgerRunDetails from './LedgerRunDetails';

export interface RunFilters {
  status: string;
  rating: string;
  actionId: string;
  modelId: string;
  /** Debounced by the caller. */
  item: string;
}

export const EMPTY_RUN_FILTERS: RunFilters = { status: '', rating: '', actionId: '', modelId: '', item: '' };

const STATUS_PILLS: Array<[string, string]> = [
  ['', 'Any status'], ['succeeded', 'Succeeded'], ['failed', 'Failed'], ['cancelled', 'Cancelled'], ['refunded', 'Refunded'],
];
const RATING_PILLS: Array<[string, string]> = [['', 'Any rating'], ['good', '👍'], ['bad', '👎'], ['none', 'Unrated']];
const RATING_ARIA: Record<string, string> = { '': 'Any rating', good: 'Rated good', bad: 'Rated bad', none: 'Unrated' };

export const LEDGER_GRID_HEADERS = ['Time', 'Merchant', 'Action', 'Item', 'Model', 'Ruby charged', 'Profit', 'Status'] as const;

/** xl: one line of 9 columns; below xl each row stacks into two lines (cells re-ordered with xl:order-*). */
const XL_COLS = 'xl:grid-cols-[88px_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,0.9fr)_116px_80px_120px_44px]';

const STATUS_TONE: Record<string, string> = {
  succeeded: 'bg-emerald-50 text-emerald-800',
  failed: 'bg-rose-50 text-rose-700',
  cancelled: 'bg-stone-100 text-stone-700',
  refunded: 'bg-amber-50 text-amber-800',
};

/** One short word for the grid chip — the full "status / settlement" is in the title and the details. */
function chipLabel(line: Pick<LedgerLine, 'status' | 'settlement'>): string {
  return line.settlement === 'refunded' ? 'refunded' : line.status;
}

const LedgerRuns: React.FC<{
  fromIso: string;
  toIso: string;
  tenantId?: string;
  filters: RunFilters;
  itemInput: string;
  onFiltersChange: (f: Partial<RunFilters>) => void;
  onItemInput: (v: string) => void;
  catalog: AiCatalogResponse | null;
  /** View-level filters from the tab (Customers / Developers, who, profile, cost). */
  extra?: Partial<LedgerFilters>;
}> = ({ fromIso, toIso, tenantId, filters, itemInput, onFiltersChange, onItemInput, catalog, extra }) => {
  const [items, setItems] = useState<LedgerLine[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, { detail: LedgerRunDetail | null; loading: boolean; error: string | null }>>({});

  const query: LedgerFilters = useMemo(() => ({
    fromIso, toIso, tenantId,
    status: filters.status || undefined,
    rating: (filters.rating || undefined) as LedgerFilters['rating'],
    actionId: filters.actionId || undefined,
    modelId: filters.modelId || undefined,
    item: filters.item.trim() || undefined,
    ...(extra ?? {}),
    limit: LEDGER_PAGE_SIZE,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [fromIso, toIso, tenantId, filters, JSON.stringify(extra ?? {})]);

  // The pager belongs to one query: any filter / window change is back on page 1
  // (derived, so a stale cursor is never sent with the new filters).
  const queryKey = JSON.stringify(query);
  const [pagerState, setPagerState] = useState<{ key: string; pager: LedgerPager }>(() => ({ key: queryKey, pager: firstPage() }));
  const pager = pagerState.key === queryKey ? pagerState.pager : firstPage();
  const setPager = (fn: (p: LedgerPager) => LedgerPager) =>
    setPagerState((s) => ({ key: queryKey, pager: fn(s.key === queryKey ? s.pager : firstPage()) }));
  useEffect(() => { setExpanded(null); }, [queryKey]);

  const cursor = pager.cursors[pager.page];
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    getRubyLedger({ ...query, cursor }).then((res) => {
      if (!live) return;
      setItems(res.items ?? []);
      setNextCursor(res.cursor);
      setTruncated(!!res.truncated);
      setPager((p) => recordPageCount(p, (res.items ?? []).length));
    }, (err: any) => {
      if (live) setError(err?.message || 'Failed to load runs');
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, pager.page, cursor]);

  const toggle = useCallback((line: LedgerLine) => {
    setExpanded((cur) => (cur === line.runId ? null : line.runId));
    if (details[line.runId]?.detail) return;
    setDetails((d) => ({ ...d, [line.runId]: { detail: null, loading: true, error: null } }));
    getRubyLedgerRun(line.tenantId, line.runId).then(
      (detail) => setDetails((d) => ({ ...d, [line.runId]: { detail, loading: false, error: null } })),
      (err: any) => setDetails((d) => ({ ...d, [line.runId]: { detail: null, loading: false, error: err?.message || 'Failed to load this run' } })),
    );
  }, [details]);

  const actionOptions = useMemo(() => actionFilterOptions(catalog?.actions ?? [], items), [catalog, items]);
  const modelOptions = useMemo(() => modelFilterOptions(catalog?.models ?? [], items), [catalog, items]);
  const labelOf = useMemo(() => {
    const m = new Map((catalog?.actions ?? []).map((a) => [a.actionId, a.label || a.actionId]));
    return (line: LedgerLine) => (line.vibeId ? `${m.get(line.actionId) ?? line.actionId} (${line.vibeId})` : m.get(line.actionId) ?? actionLabel(line));
  }, [catalog]);

  const range = pageRange(pager, items.length);

  return (
    <div className="bg-white border border-stone-200 rounded-xl text-stone-900" data-testid="ledger-runs">
      {/* One light filter row. */}
      <div className="px-3 md:px-4 py-3 border-b border-stone-100 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Status">
          {STATUS_PILLS.map(([v, label]) => (
            <Pill key={`s-${v}`} active={filters.status === v} onClick={() => onFiltersChange({ status: v })}>{label}</Pill>
          ))}
        </div>
        <span className="hidden md:block h-6 w-px bg-stone-200" aria-hidden />
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Rating">
          {RATING_PILLS.map(([v, label]) => (
            <Pill key={`r-${v}`} active={filters.rating === v} ariaLabel={RATING_ARIA[v]} onClick={() => onFiltersChange({ rating: v })}>{label}</Pill>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:ml-auto">
          <div className="w-full sm:w-[190px]">
            <SearchableDropdown
              options={actionOptions}
              value={filters.actionId || ALL}
              onChange={(v) => onFiltersChange({ actionId: !v || v === ALL ? '' : v })}
              ariaLabel="Filter by action"
              searchPlaceholder="Search actions…"
            />
          </div>
          <div className="w-full sm:w-[190px]">
            <SearchableDropdown
              options={modelOptions}
              value={filters.modelId || ALL}
              onChange={(v) => onFiltersChange({ modelId: !v || v === ALL ? '' : v })}
              ariaLabel="Filter by model"
              searchPlaceholder="Search models…"
            />
          </div>
          <input
            type="text"
            value={itemInput}
            onChange={(e) => onItemInput(e.target.value)}
            placeholder="Item name or id"
            aria-label="Filter by item"
            className="w-full sm:w-[170px] min-h-[44px] px-3 rounded-lg border border-stone-200 bg-white text-[13px] text-stone-900 placeholder:text-stone-400 outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-rose-100"
          />
        </div>
      </div>

      {error && <div className="p-3"><ErrorBanner title="Failed to load runs" detail={error} /></div>}

      {/* Header — only when the grid is one line per row. */}
      <div role="row" className={`hidden xl:grid ${XL_COLS} gap-x-3 px-4 py-2 border-b border-stone-100 text-[11px] uppercase tracking-wide text-stone-500 font-semibold`}>
        {LEDGER_GRID_HEADERS.map((h) => (
          <span key={h} className={`text-stone-500 ${h === 'Ruby charged' || h === 'Profit' ? 'text-right' : ''}`}>{h}</span>
        ))}
        <span className="sr-only">Details</span>
      </div>

      <div className="relative min-h-[120px]">
        {loading ? (
          <RubyLoader variant="section" />
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-[13px] text-stone-500">
            {truncated && nextCursor ? 'No matches in the runs scanned so far — Next keeps looking further back.' : 'No AI runs match these filters in this period.'}
          </p>
        ) : (
          <div role="rowgroup" className="divide-y divide-stone-100">
            {items.map((line) => (
              <LedgerGridRow
                key={line.runId}
                line={line}
                actionText={labelOf(line)}
                open={expanded === line.runId}
                onToggle={() => toggle(line)}
                detailState={details[line.runId]}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pager. */}
      <div className="px-3 md:px-4 py-2 border-t border-stone-100 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-stone-500" aria-live="polite">
          <span className="text-stone-800 font-semibold">Page {pager.page + 1}</span>
          {items.length > 0 && <> · runs {range.first}–{range.last}</>}
          <> · {range.shownSoFar} shown so far</>
          {truncated && <span className="text-amber-800"> · scan limit reached on this page</span>}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPager((p) => prevPage(p))}
            disabled={pager.page === 0 || loading}
            className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-lg border border-stone-200 bg-white text-[12px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <button
            type="button"
            onClick={() => setPager((p) => nextPage(p, nextCursor))}
            disabled={!nextCursor || loading}
            className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-lg border border-stone-200 bg-white text-[12px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const LedgerGridRow: React.FC<{
  line: LedgerLine;
  actionText: string;
  open: boolean;
  onToggle: () => void;
  detailState?: { detail: LedgerRunDetail | null; loading: boolean; error: string | null };
}> = ({ line, actionText, open, onToggle, detailState }) => {
  const loc = locationLabel(line);
  const isDev = line.params?.kind === 'developer';
  const statusTone = STATUS_TONE[line.settlement === 'refunded' ? 'refunded' : line.status] ?? 'bg-stone-100 text-stone-700';
  return (
    <div role="row" aria-expanded={open} className={open ? 'bg-rose-50/30' : ''}>
      <div
        onClick={onToggle}
        className={`grid grid-cols-[minmax(0,1fr)_44px] ${XL_COLS} gap-x-3 items-center px-3 md:px-4 py-1.5 min-h-[44px] cursor-pointer hover:bg-stone-50`}
      >
        <div className="min-w-0 xl:contents">
          {/* Line 1 below xl: time · merchant · charged · profit */}
          <div className="flex items-baseline gap-3 min-w-0 xl:contents">
            <span data-cell className="xl:order-1 shrink-0 text-[12px] text-stone-500 tabular-nums">{formatTime(line.createdAt)}</span>
            <span data-cell className="xl:order-2 min-w-0 flex-1 truncate text-[13px] text-stone-900" title={isDev ? (line.actorEmail || line.actorId) : line.tenantId}>
              {isDev ? (
                <>
                  <span className="font-semibold text-stone-900">{String(line.params?.developer || line.actorEmail || line.actorId || 'Developer')}</span>
                  <span className="text-[11px] text-stone-500"> · Developer{line.params?.profileName ? ` · ${line.params.profileName}` : ''}</span>
                </>
              ) : (
                <>
                  <span className="font-semibold text-stone-900">{merchantName(line)}</span>
                  {loc && <span className="text-[11px] text-stone-500"> · {loc}</span>}
                </>
              )}
            </span>
            <span data-cell className="xl:order-6 shrink-0 text-right text-[12.5px] text-stone-900 tabular-nums">
              {isDev ? (
                <span title="What this developer run cost us (USD)">
                  {money(line.vendorCostUsd ?? 0)}
                  {line.params?.cost === 'estimated' && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800" title="Estimated at dispatch — the real cost replaces it when the run reports back">est.</span>}
                </span>
              ) : line.free
                ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800">Free AI</span>
                : tokensLabel(line.tokensCharged ?? 0)}
            </span>
            <span data-cell className={`xl:order-7 shrink-0 text-right text-[12.5px] font-semibold tabular-nums ${profitTone(line.profitUsd) === 'negative' ? 'text-rose-600' : 'text-emerald-700'}`}>
              {money(line.profitUsd)}
            </span>
          </div>
          {/* Line 2 below xl: action · item · model · status */}
          <div className="flex items-center gap-3 min-w-0 text-[12px] xl:contents">
            <span data-cell className="xl:order-3 min-w-0 truncate text-[12.5px] text-stone-800" title={line.actionId}>{actionText}</span>
            <span data-cell className="xl:order-4 min-w-0 truncate text-[12.5px] text-stone-800" title={itemLabel(line.target)}>{itemLabel(line.target) || '—'}</span>
            <span data-cell className="xl:order-5 min-w-0 truncate text-[12px] text-stone-500" title={modelLabel(line)}>{shortModel(line) || '—'}</span>
            <span data-cell className="xl:order-8 shrink-0 min-w-0 text-stone-700">
              <span title={statusLabel(line)} className={`inline-block max-w-full truncate text-[10.5px] uppercase tracking-wide px-2 py-0.5 rounded-full ${statusTone}`}>
                {chipLabel(line)}
              </span>
            </span>
          </div>
        </div>
        <button
          type="button"
          aria-label={open ? `Hide details for run ${line.runId}` : `Show details for run ${line.runId}`}
          aria-expanded={open}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="xl:order-9 justify-self-end min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-stone-400 hover:text-stone-800"
        >
          <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <LedgerRunDetails
          line={line}
          detail={detailState?.detail ?? null}
          loading={detailState?.loading ?? true}
          error={detailState?.error ?? null}
        />
      )}
    </div>
  );
};

export default LedgerRuns;
